import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { describeRule, shortRuleName } from '../lib/describe';
import { ruleFromStarter } from '../lib/ruleTemplates';
import RuleStarterPicker from '../components/RuleStarterPicker';
import RuleOptions from '../components/RuleOptions';
import HelpTip from '../components/HelpTip';
import { validateRule } from '../lib/validateRule';
import { unsafeRuleStops } from '../lib/ruleSafety';
import {
  CATALOG_DISCOVERED_AT,
  SYSTEM_RULES
} from '../config/rateShopConstants';
import { useShipStationTags } from '../hooks/useShipStationTags';
import RuleConditions from '../components/RuleConditions';
import ActionBuilder from '../components/ActionBuilder';
import RulePreview from '../components/RulePreview';
import { ConfirmModal, dateTime, LoadError } from '../components/Common';

const RuleEditor = ({
  rule,
  rules,
  nextPriority,
  canEdit,
  saveRule,
  deleteRule,
  settings,
  getDecision,
  onHide,
  storesData
}) => {
  const [draft, setDraft] = useState(() =>
    rule
      ? {
          ...rule,
          match: {
            all: (rule.match?.all || []).map(c => ({ ...c })),
            any: (rule.match?.any || []).map(c => ({ ...c }))
          },
          actions: (rule.actions || []).map(a => ({ ...a }))
        }
      : {
          name: '',
          enabled: true,
          priority: nextPriority,
          stopProcessing: false,
          system: false,
          match: { all: [], any: [] },
          actions: [],
          notes: ''
        }
  );
  const [started, setStarted] = useState(Boolean(rule));
  const [autoName, setAutoName] = useState(!rule);
  const [starterTitle, setStarterTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const { tagNames, loading: tagsLoading } = useShipStationTags();
  const validation = validateRule(draft);
  const warnings = unsafeRuleStops([
    { ...draft, id: draft.id || 'preview-rule' },
    ...(rules.length ? rules : SYSTEM_RULES).filter(r => r.id !== draft.id)
  ]);
  const sentence = describeRule(draft, storesData.stores);
  const suggestedName = starterTitle || shortRuleName(draft, storesData.stores);
  useEffect(() => {
    if (autoName && started)
      setDraft(prev =>
        prev.name === suggestedName ? prev : { ...prev, name: suggestedName }
      );
  }, [suggestedName, autoName, started]);
  const disabled = !canEdit || busy;
  const update = patch => setDraft(prev => ({ ...prev, ...patch }));

  const save = async () => {
    if (!canEdit || !validation.ok || busy) return;
    setBusy(true);
    setError(null);
    try {
      await saveRule(draft);
      toast.success('Rule saved');
      setBusy(false);
      onHide();
    } catch (err) {
      setError(err);
      toast.error(err.message);
      setBusy(false);
      setConfirm(null);
    }
  };
  const remove = async () => {
    if (!canEdit || !rule?.id || busy) return;
    setBusy(true);
    try {
      await deleteRule(rule.id);
      toast.success('Rule deleted');
      setBusy(false);
      onHide();
    } catch (err) {
      setError(err);
      toast.error(err.message);
      setBusy(false);
      setConfirm(null);
    }
  };
  const requestSave = e => {
    e.preventDefault();
    if (rule?.system && rule.enabled !== false && draft.enabled === false)
      setConfirm('disable');
    else save();
  };

  return (
    <>
      <Modal
        show
        onHide={busy ? undefined : onHide}
        size="xl"
        backdrop="static"
        scrollable
      >
        <Modal.Header closeButton={!busy}>
          <Modal.Title>
            {rule ? 'Rule details' : 'New rule'}{' '}
            {rule?.system && <Badge bg="secondary">System</Badge>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!canEdit && (
            <Alert variant="info">
              Read-only access. Only Topicz operations users can change rules.
            </Alert>
          )}
          <LoadError error={error} />
          {warnings.map(({ rule: stop, systemRules }) => (
            <Alert variant="warning" key={stop.id}>
              “{stop.name}” stops processing without skipping the order before
              these enabled system rules:{' '}
              {systemRules.map(r => r.name).join(', ')}. Orders may bypass their
              safety checks.
            </Alert>
          ))}
          {!started ? (
            <RuleStarterPicker
              onSelect={id => {
                const next = ruleFromStarter(id, nextPriority);
                setDraft(next);
                setAutoName(true);
                setStarterTitle(next.name);
                setStarted(true);
              }}
            />
          ) : (
            <>
              {!rule && (
                <Button
                  variant="link"
                  size="sm"
                  type="button"
                  disabled={disabled}
                  onClick={() => setStarted(false)}
                >
                  Choose a different starting point
                </Button>
              )}
              <Alert variant="primary" className="mt-2">
                <strong>In plain words</strong>
                <p className="mb-0 mt-1">{sentence}</p>
              </Alert>
              <Form id="rate-shop-rule-form" onSubmit={requestSave}>
                <Form.Group className="mb-4" controlId="rate-shop-rule-name">
                  <Form.Label>
                    Rule name{' '}
                    <HelpTip label="Rule name">
                      A name is suggested from your choices. Edit it to use your
                      own words.
                    </HelpTip>
                  </Form.Label>
                  <Form.Control
                    required
                    disabled={disabled}
                    value={draft.name}
                    onChange={e => {
                      setAutoName(false);
                      update({ name: e.target.value });
                    }}
                  />
                  {!autoName && !disabled && (
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      onClick={() => setAutoName(true)}
                    >
                      Use suggested name
                    </Button>
                  )}
                </Form.Group>
                <h5>Which orders should this apply to?</h5>
                <p className="small text-muted">
                  Add conditions to choose which orders qualify. Use all for
                  requirements together, or any for alternatives.
                </p>
                <RuleConditions
                  match={draft.match}
                  disabled={disabled}
                  tagNames={tagNames}
                  tagsLoading={tagsLoading}
                  stores={storesData.stores}
                  onChange={match => update({ match })}
                />
                {!draft.match.all.length && !draft.match.any.length && (
                  <Alert variant="warning">
                    This rule applies to every order. Add a condition if it
                    should only apply to some orders.
                  </Alert>
                )}
                <ActionBuilder
                  settings={settings}
                  actions={draft.actions}
                  disabled={disabled}
                  tagNames={tagNames}
                  tagsLoading={tagsLoading}
                  onChange={actions => update({ actions })}
                />
                <RuleOptions
                  draft={draft}
                  update={update}
                  disabled={disabled}
                />
                {!validation.ok && (
                  <Alert variant="warning">
                    <strong>Before saving:</strong>
                    <ul className="mb-0">
                      {validation.errors.map((message, i) => (
                        <li key={i}>{message}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
              </Form>
              <RulePreview
                draft={draft}
                rules={rules}
                settings={settings}
                getDecision={getDecision}
                valid={validation.ok}
                stores={storesData.stores}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <span className="small text-muted me-auto">
            Carrier catalogue: {dateTime(CATALOG_DISCOVERED_AT)}
          </span>
          {canEdit && rule?.id && (
            <Button
              variant="outline-danger"
              disabled={busy || rules.length <= 1}
              title={
                rules.length <= 1
                  ? 'The last remaining rule cannot be deleted. Disable it instead.'
                  : undefined
              }
              onClick={() => setConfirm('delete')}
            >
              Delete rule
            </Button>
          )}
          <Button variant="secondary" disabled={busy} onClick={onHide}>
            Close
          </Button>
          {canEdit && started && (
            <Button
              type="submit"
              form="rate-shop-rule-form"
              disabled={busy || !validation.ok}
            >
              {busy ? 'Saving…' : 'Save rule'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      <ConfirmModal
        show={Boolean(confirm)}
        title={confirm === 'delete' ? 'Delete rule?' : 'Disable system rule?'}
        busy={busy}
        onHide={() => setConfirm(null)}
        onConfirm={confirm === 'delete' ? remove : save}
        confirmLabel={confirm === 'delete' ? 'Delete rule' : 'Disable and save'}
      >
        {confirm === 'delete' ? (
          <>
            <p>Delete “{draft.name}”? This removes the rule permanently.</p>
            {rule?.system && (
              <Alert variant="warning" className="mb-0">
                This is a system safety rule. We recommend disabling it instead,
                so it can be restored easily.
              </Alert>
            )}
          </>
        ) : (
          <p>
            “{draft.name}” is a system safety rule. Disabling it may allow
            orders previously protected by this rule to be changed.
          </p>
        )}
      </ConfirmModal>
    </>
  );
};

export default RuleEditor;
